import { Node, mergeAttributes } from "@tiptap/core";
import { keydownHandler } from "@tiptap/pm/keymap";
import { PluginKey } from "@tiptap/pm/state";
import Suggestion from "@tiptap/suggestion";
import { DisallowCharactersPlugin } from "./dissallowedCharacters";

const TagPluginKey = new PluginKey("tag");
const Tag = Node.create({
    name: "cw-tag",
    group: "inline",
    inline: true,
    selectable: false,
    atom: true,
    addOptions() {
        return {
            renderLabel({ options, node }) {
                return `${options.suggestion.char}${node.attrs.name}`;
            },
            suggestion: {
                char: "#",
                allowSpaces: false,
                pluginKey: TagPluginKey,
                command: ({ editor, range, props }) => {
                    const tagContent = [
                        {
                            type: this.name,
                            attrs: { tagid: props.id, name: props.tag },
                        },
                        { type: "text", text: " " },
                    ];

                    editor
                        .chain()
                        .focus()
                        .insertContentAt(range, tagContent)
                        .run();
                },
            },
        };
    },
    addAttributes() {
        // add tagid and name attributes on the note so that they are
        // accessible and usable in renderHTML method down below
        return {
            tagid: {
                renderHTML: (attributes) => ({
                    tagid: attributes.tagid,
                }),
            },
            name: {
                renderHTML: (attributes) => ({
                    name: attributes.name,
                }),
            },
        };
    },
    parseHTML() {
        // render cw-tag with tagid and name attributes
        return [
            {
                tag: "cw-tag",
            },
        ];
    },
    renderHTML({ node }) {
        // render cw-tag with a child span element and proper attributes
        // this is done in order to achieve backwards compatibility with
        // the existing tag implementation
        const tagAttributes = {
            name: node.attrs.name,
            tagid: node.attrs.tagid,
        };
        const spanContent = [
            "span",
            { contenteditable: "false" },
            this.options.renderLabel({ options: this.options, node }),
        ];

        return ["cw-tag", mergeAttributes(tagAttributes), spanContent];
    },
    addKeyboardShortcuts() {
        return {
            Backspace: () =>
                this.editor.commands.command(({ tr, state }) => {
                    let isMention = false;
                    const { selection } = state;
                    const { empty, anchor } = selection;
                    if (!empty) {
                        return false;
                    }
                    state.doc.nodesBetween(anchor - 1, anchor, (node, pos) => {
                        if (node.type.name === this.name) {
                            isMention = true;
                            tr.insertText(
                                this.options.suggestion.char,
                                pos,
                                pos + node.nodeSize,
                            );
                            return false;
                        }
                    });
                    return isMention;
                }),
        };
    },
    addProseMirrorPlugins() {
        return [
            Suggestion({
                editor: this.editor,
                ...this.options.suggestion,
            }),
            DisallowCharactersPlugin(/[^a-zA-Z0-9 _-]/),
        ];
    },
});

export { Tag };
